import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import Modal from 'react-bootstrap/Modal';
import toastr from 'toastr';
import postal from 'postal';
import defaultOnErrorHandler from '../../shared/default-on-error-handler';
import ErrorBoundary from '../../shared/error-boundary';
import move from '../../shared/move';
import Loading from '../../shared/loading';
import { getPicks, updatePicks } from '../../services/editors-picks-service';

const DragHandle = SortableHandle(() => <i className="fa fa-bars" />);

const EditorsPickItem = SortableElement(({ pick, removePick }) => (
  <li className="manage-editors-picks-list-item">
    <div className="controls">
      <DragHandle />
      <button type="button" onClick={() => removePick(pick)} className="fa fa-trash" />
    </div>
    <div className="manage-editors-picks-list-item-content">
      <h5>{pick.wire.title}</h5>
      <img className="avatar avatar--large" src={pick.wire.profile?.profile_picture_url} alt="Avatar" />
      {!!pick.wire.profile?.name && <p className="author">{pick.wire.profile?.name}</p>}
      {!!pick.wire.profile?.organisation && <p className="organization">{pick.wire.profile?.organisation}</p>}
    </div>
  </li>
));

const EditorsPicksList = SortableContainer(({ picks, removePick }) => (
  <ul>
    {picks.map((pick, index) => (
      <EditorsPickItem key={`pick-${pick.id}`} {...{ index, pick, removePick }} />
    ))}
  </ul>
));

function reIndex(d, i) {
  d.order_index = i;
}

export default class ManageEditorsPicksModal extends React.Component {
  static propTypes = {
    onDismiss: PropTypes.func,
  };

  static defaultProps = {
    onDismiss: null,
  };

  constructor(props, context) {
    super(props, context);

    this.save = this.save.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.onSortEnd = this.onSortEnd.bind(this);
    this.removePick = this.removePick.bind(this);

    this.state = {
      picks: [],
      show: false,
    };
  }

  componentDidMount() {
    postal.subscribe({
      channel: 'commands.editors-picks',
      topic: 'pick-wire',
      callback: data => this.present(data.wire),
    });
    postal.subscribe({
      channel: 'commands.editors-picks',
      topic: 'manage-picks',
      callback: () => this.present(null),
    });
  }

  onSortEnd({ oldIndex, newIndex }) {
    this.setState(state => {
      state.picks = move(state.picks, oldIndex, newIndex);
      state.picks.forEach(reIndex);
      return state;
    });
  }

  dismiss(event) {
    event && event.preventDefault();
    this.setState({ show: false });
    this.props.onDismiss && this.props.onDismiss();
  }

  present(newWire) {
    if (newWire) newWire.order_index = 0;

    this.setState({ show: true }, () => {
      this.loading.start();

      getPicks()
        .then(result => {
          const existingPicks = _.sortBy(result.data, p => p.order_index);
          const newWireData = {
            id: newWire.wire_id,
            order_index: 0,
            wire: {
              title: newWire.title,
              profile: {
                name: newWire.profile ? newWire.profile.name : newWire.wire.profile.name,
                profile_picture_url: (newWire.profile ? newWire.profile.profile_picture_url : newWire.wire.profile.profile_picture_url) || window.getLivewireAssetPath('avatar-fallback.png'),
              },
            },
            wire_id: newWire.wire_id,
          };
          const picks = !newWire || _.some(existingPicks, p => p.wire_id === newWire.wire_id)
            ? existingPicks
            : [newWireData, ...existingPicks];

          picks.forEach(reIndex);

          this.setState({ picks });
        })
        .catch(e => defaultOnErrorHandler(e, "Error getting Editors's Picks"))
        .finally(() => this.loading.stop());
    });
  }

  save(event) {
    event && event.preventDefault();

    this.loading.start();

    updatePicks(this.state.picks.map(p => _.pick(p, 'wire_id', 'order_index')))
      .then(() => {
        toastr.success("Editor's Picks have been updated");
        this.loading.stop();
        this.dismiss();
      })
      .catch(e => {
        this.loading.stop();
        defaultOnErrorHandler(e, "Error updating Editor's Picks");
      });
  }

  removePick(pick) {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to remove this wire?')) return;

    this.setState(state => {
      state.picks = _.without(state.picks, pick);
      return state;
    });
  }

  render() {
    return (
      <ErrorBoundary>
        <Modal
          centered="true"
          dialogAs="div"
          dialogClassName="manage-editors-picks-modal"
          show={this.state.show}
          backdrop
          onHide={this.dismiss}
        >
          <article>
            <header>
              <h4>Editor&apos;s Picks</h4>
            </header>
            <Loading ref={el => (this.loading = el)}>
              <section>
                <EditorsPicksList
                  helperClass="sortable-helper"
                  picks={this.state.picks}
                  onSortEnd={this.onSortEnd}
                  useDragHandle
                  removePick={this.removePick}
                />
              </section>
              <footer>
                <button type="button" className="btn btn-secondary" onClick={this.dismiss}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={this.save}>Save</button>
              </footer>
            </Loading>
          </article>
        </Modal>
      </ErrorBoundary>
    );
  }
}
